import React, { useState, useEffect, useRef } from 'react';
import axios from './lib/axiosClient';

const C = {
  bg: '#0f0f11', panel: '#16161a', border: '#2a2a35', text: '#f0f0f2',
  muted: '#7a7a8a', amber: '#f5a623', green: '#3ecf8e', red: '#e8484a',
  blue: '#4f8ef7', purple: '#a78bfa',
};
const inp = { width: '100%', padding: '9px 12px', background: '#1a1a22', border: '1px solid ' + C.border, borderRadius: 8, color: C.text, fontSize: 14, boxSizing: 'border-box', outline: 'none' };

function StatusBadge({ status }) {
  const colors = { active: C.green, paused: C.amber, cancelled: C.red, expired: C.muted };
  const icons = { active: '✅', paused: '⏸️', cancelled: '❌', expired: '⏱️' };
  const color = colors[status] || C.muted;
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 6, background: color + '20', color, textTransform: 'uppercase' }}>
      {icons[status]} {status}
    </span>
  );
}

function CustomerDropdown({ customers, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef();
  const selected = customers.find(c => String(c.id) === String(value));
  const filtered = customers.filter(c => {
    const name = (c.first_name + ' ' + (c.last_name || '')).toLowerCase();
    const phone = c.phone || '';
    return !search || name.includes(search.toLowerCase()) || phone.includes(search);
  });

  useEffect(() => {
    function h(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const displayName = selected ? (selected.first_name + ' ' + (selected.last_name || '')).trim() : null;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div onClick={() => setOpen(!open)} style={{ ...inp, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {displayName ? (
          <div>
            <div style={{ fontWeight: 700 }}>{displayName}</div>
            {selected?.phone && <div style={{ fontSize: 11, color: C.muted }}>{selected.phone}</div>}
          </div>
        ) : <span style={{ color: C.muted }}>Search customers...</span>}
        <span style={{ color: C.muted, fontSize: 12 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: C.panel, border: '1px solid ' + C.border, borderRadius: 10, zIndex: 200, boxShadow: '0 8px 24px rgba(0,0,0,0.5)', marginTop: 4 }}>
          <div style={{ padding: 8 }}>
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Search by name or phone..." style={{ ...inp, fontSize: 13, padding: '7px 10px' }} />
          </div>
          <div style={{ maxHeight: 240, overflowY: 'auto' }}>
            {filtered.length === 0 ? <div style={{ padding: 16, textAlign: 'center', color: C.muted, fontSize: 13 }}>No customers found</div>
            : filtered.slice(0, 20).map(c => {
              const name = (c.first_name + ' ' + (c.last_name || '')).trim();
              return (
                <div key={c.id} onClick={() => { onChange(c.id); setOpen(false); setSearch(''); }}
                  style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #1a1a22', background: String(c.id) === String(value) ? 'rgba(245,166,35,0.08)' : 'transparent' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(245,166,35,0.08)'}
                  onMouseLeave={e => e.currentTarget.style.background = String(c.id) === String(value) ? 'rgba(245,166,35,0.08)' : 'transparent'}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{name}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                    {[c.phone, c.email].filter(Boolean).join(' · ')}
                    {c.is_vip ? ' ⭐ VIP' : ''}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Memberships() {
  const [tab, setTab] = useState('plans');
  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [stats, setStats] = useState({});
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showEnroll, setShowEnroll] = useState(false);
  const [editPlan, setEditPlan] = useState(null);
  const [planForm, setPlanForm] = useState({ name: '', description: '', price: '', billing_interval: 'monthly', visits_per_period: '', perks: '' });
  const [enrollForm, setEnrollForm] = useState({ contact_id: '', plan_id: '', notes: '' });
  const [memberFilter, setMemberFilter] = useState('all');
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const [pRes, sRes, stRes, cRes] = await Promise.all([
        axios.get('/api/memberships/plans'),
        axios.get('/api/memberships/subscriptions'),
        axios.get('/api/memberships/stats'),
        axios.get('/api/customers'),
      ]);
      if (pRes.data.success) setPlans(pRes.data.plans || []);
      if (sRes.data.success) setSubscriptions(sRes.data.subscriptions || []);
      if (stRes.data.success) setStats(stRes.data.stats || {});
      setCustomers(cRes.data.customers || cRes.data || []);
    } catch(e) {}
    setLoading(false);
  }

  async function savePlan() {
    if (!planForm.name || !planForm.price) return alert('Name and price required');
    try {
      if (editPlan) await axios.put('/api/memberships/plans/' + editPlan.id, planForm);
      else await axios.post('/api/memberships/plans', planForm);
      closePlanModal(); loadAll();
    } catch(e) { alert('Error saving plan'); }
  }

  function closePlanModal() {
    setShowPlanModal(false); setEditPlan(null);
    setPlanForm({ name: '', description: '', price: '', billing_interval: 'monthly', visits_per_period: '', perks: '' });
  }

  function openEditPlan(p) {
    setEditPlan(p);
    setPlanForm({ name: p.name, description: p.description || '', price: p.price, billing_interval: p.billing_interval, visits_per_period: p.visits_per_period || '', perks: p.perks || '' });
    setShowPlanModal(true);
  }

  async function deletePlan(id) {
    if (!window.confirm('Deactivate this plan?')) return;
    await axios.delete('/api/memberships/plans/' + id); loadAll();
  }

  async function enrollMember() {
    if (!enrollForm.contact_id || !enrollForm.plan_id) return alert('Select a customer and plan');
    try {
      await axios.post('/api/memberships/subscriptions', enrollForm);
      // Auto-mark customer as VIP
      if (enrollForm.contact_id) {
        try { await axios.put('/api/customers/' + enrollForm.contact_id, { is_vip: true }); } catch(e) {}
      }
      setShowEnroll(false); setEnrollForm({ contact_id: '', plan_id: '', notes: '' }); loadAll();
    } catch(e) { alert('Error enrolling member'); }
  }

  async function updateSubStatus(id, status) {
    const labels = { cancelled: 'Cancel', paused: 'Pause', active: 'Reactivate' };
    if (!window.confirm(labels[status] + ' this subscription?')) return;
    await axios.patch('/api/memberships/subscriptions/' + id, { status }); loadAll();
  }

  const fmt = n => '$' + parseFloat(n || 0).toFixed(2);

  const filteredSubs = memberFilter === 'all' ? subscriptions : subscriptions.filter(s => s.status === memberFilter);

  // Upcoming renewals (next 7 days)
  const upcoming = subscriptions.filter(s => {
    if (s.status !== 'active' || !s.next_billing) return false;
    const diff = (new Date(s.next_billing) - new Date()) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= 7;
  });

  const getCustomerName = (sub) => {
    if (sub.contacts?.name) return sub.contacts.name;
    const c = customers.find(c => String(c.id) === String(sub.contact_id));
    return c ? (c.first_name + ' ' + (c.last_name || '')).trim() : 'Unknown';
  };

  if (loading) return <div style={{ padding: 60, textAlign: 'center', color: C.muted }}>Loading...</div>;

  return (
    <div style={{ padding: 24, background: C.bg, minHeight: '100vh', color: C.text }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800 }}>👑 Memberships</div>
          <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>Manage plans and member subscriptions</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setShowEnroll(true)} style={{ padding: '9px 18px', background: 'transparent', color: C.amber, border: '1px solid ' + C.amber, borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}>+ Enroll Member</button>
          <button onClick={() => setShowPlanModal(true)} style={{ padding: '9px 18px', background: C.amber, color: '#000', border: 'none', borderRadius: 10, fontWeight: 800, cursor: 'pointer' }}>+ Create Plan</button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 24 }}>
        {[
          ['Active Members', stats.active || 0, C.green, '👤'],
          ['Monthly Revenue', fmt(stats.mrr), C.amber, '💰'],
          ['Paused', stats.paused || 0, C.amber, '⏸️'],
          ['Total Plans', plans.filter(p => p.is_active).length, C.blue, '📋'],
        ].map(([label, val, color, icon]) => (
          <div key={label} style={{ background: C.panel, border: '1px solid ' + C.border, borderRadius: 12, padding: '18px 20px' }}>
            <div style={{ fontSize: 20, marginBottom: 8 }}>{icon}</div>
            <div style={{ fontSize: 24, fontWeight: 900, color, marginBottom: 4 }}>{val}</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase' }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Upcoming renewals alert */}
      {upcoming.length > 0 && (
        <div style={{ background: 'rgba(245,166,35,0.08)', border: '1px solid rgba(245,166,35,0.25)', borderRadius: 12, padding: '14px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 20 }}>⏰</span>
          <div>
            <div style={{ fontWeight: 700, color: C.amber }}>{upcoming.length} renewal{upcoming.length > 1 ? 's' : ''} in the next 7 days</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{upcoming.map(s => getCustomerName(s)).join(', ')}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[['plans', '📋 Plans', plans.filter(p => p.is_active).length], ['members', '👤 Members', subscriptions.length]].map(([id, label, count]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{ padding: '9px 20px', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer', background: tab === id ? C.amber : C.panel, color: tab === id ? '#000' : C.muted }}>
            {label} ({count})
          </button>
        ))}
      </div>

      {/* Plans Tab */}
      {tab === 'plans' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {plans.filter(p => p.is_active).length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: C.muted }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>👑</div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>No plans yet</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>Create your first membership plan</div>
            </div>
          ) : plans.filter(p => p.is_active).map(plan => {
            const memberCount = subscriptions.filter(s => s.plan_id === plan.id && s.status === 'active').length;
            const mrr = memberCount * parseFloat(plan.price || 0);
            return (
              <div key={plan.id} style={{ background: C.panel, border: '1px solid ' + C.border, borderRadius: 14, overflow: 'hidden' }}>
                <div style={{ padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{plan.name}</div>
                    {plan.description && <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>{plan.description}</div>}
                    {plan.perks && <div style={{ fontSize: 12, color: C.purple, marginTop: 6 }}>✨ {plan.perks}</div>}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 30, fontWeight: 900, color: C.amber }}>{fmt(plan.price)}</div>
                    <div style={{ fontSize: 12, color: C.muted }}>per {plan.billing_interval}</div>
                  </div>
                </div>
                <div style={{ padding: '12px 24px', background: 'rgba(0,0,0,0.2)', borderTop: '1px solid ' + C.border, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: 20 }}>
                    <span style={{ fontSize: 13, color: C.muted }}>👤 <strong style={{ color: C.text }}>{memberCount}</strong> active</span>
                    <span style={{ fontSize: 13, color: C.muted }}>💰 <strong style={{ color: C.green }}>{fmt(mrr)}</strong>/mo</span>
                    <span style={{ fontSize: 13, color: C.muted }}>{plan.visits_per_period ? plan.visits_per_period + ' visits' : '♾️ Unlimited'}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => { setEnrollForm(f => ({...f, plan_id: plan.id})); setShowEnroll(true); }}
                      style={{ padding: '6px 14px', background: 'rgba(62,207,142,0.1)', border: '1px solid rgba(62,207,142,0.2)', borderRadius: 8, color: C.green, cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>+ Enroll</button>
                    <button onClick={() => openEditPlan(plan)} style={{ padding: '6px 14px', background: 'transparent', border: '1px solid ' + C.border, borderRadius: 8, color: C.muted, cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>✏️ Edit</button>
                    <button onClick={() => deletePlan(plan.id)} style={{ padding: '6px 14px', background: 'rgba(232,72,74,0.1)', border: '1px solid rgba(232,72,74,0.2)', borderRadius: 8, color: C.red, cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>🗑</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Members Tab */}
      {tab === 'members' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {['all','active','paused','cancelled'].map(f => (
              <button key={f} onClick={() => setMemberFilter(f)}
                style={{ padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12, background: memberFilter === f ? C.amber : C.panel, color: memberFilter === f ? '#000' : C.muted }}>
                {f.charAt(0).toUpperCase()+f.slice(1)} {f === 'all' ? '('+subscriptions.length+')' : '('+subscriptions.filter(s=>s.status===f).length+')'}
              </button>
            ))}
          </div>
          {filteredSubs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: C.muted }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>👤</div>
              <div>No members yet — enroll your first member!</div>
            </div>
          ) : filteredSubs.map(sub => {
            const name = getCustomerName(sub);
            const plan = plans.find(p => p.id === sub.plan_id);
            const renewingSoon = sub.next_billing && (new Date(sub.next_billing) - new Date()) / (1000*60*60*24) <= 7;
            return (
              <div key={sub.id} style={{ background: C.panel, border: '1px solid ' + (renewingSoon && sub.status === 'active' ? 'rgba(245,166,35,0.4)' : C.border), borderRadius: 14, padding: '16px 20px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 42, height: 42, borderRadius: 10, background: C.amber + '20', border: '1px solid ' + C.amber + '40', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, color: C.amber, flexShrink: 0 }}>
                  {name.charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 800, fontSize: 15 }}>{name}</span>
                    <StatusBadge status={sub.status} />
                    {renewingSoon && sub.status === 'active' && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 5, background: 'rgba(245,166,35,0.15)', color: C.amber }}>⏰ Renews soon</span>}
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
                    {plan?.name || sub.membership_plans?.name || 'Unknown Plan'} · {fmt(plan?.price || sub.membership_plans?.price)}/{plan?.billing_interval || 'month'}
                    {sub.visits_limit ? ` · ${sub.visits_used||0}/${sub.visits_limit} visits` : ''}
                  </div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                    Started {sub.started_at ? new Date(sub.started_at).toLocaleDateString() : '—'}
                    {sub.next_billing ? ' · Renews ' + new Date(sub.next_billing).toLocaleDateString() : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  {sub.status === 'active' && <>
                    <button onClick={() => updateSubStatus(sub.id, 'paused')} style={{ padding: '6px 12px', background: 'transparent', border: '1px solid ' + C.border, borderRadius: 8, color: C.muted, cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>⏸ Pause</button>
                    <button onClick={() => updateSubStatus(sub.id, 'cancelled')} style={{ padding: '6px 12px', background: 'rgba(232,72,74,0.1)', border: '1px solid rgba(232,72,74,0.2)', borderRadius: 8, color: C.red, cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>Cancel</button>
                  </>}
                  {sub.status === 'paused' && <button onClick={() => updateSubStatus(sub.id, 'active')} style={{ padding: '6px 12px', background: 'rgba(62,207,142,0.1)', border: '1px solid rgba(62,207,142,0.2)', borderRadius: 8, color: C.green, cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>▶ Resume</button>}
                  {sub.status === 'cancelled' && <button onClick={() => updateSubStatus(sub.id, 'active')} style={{ padding: '6px 12px', background: 'rgba(79,142,247,0.1)', border: '1px solid rgba(79,142,247,0.2)', borderRadius: 8, color: C.blue, cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>🔄 Reactivate</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Plan Modal */}
      {showPlanModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: C.panel, border: '1px solid ' + C.border, borderRadius: 16, width: '100%', maxWidth: 520, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid ' + C.border, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 17, fontWeight: 800 }}>{editPlan ? '✏️ Edit Plan' : '👑 New Plan'}</div>
              <button onClick={closePlanModal} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 22, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div style={{ gridColumn: '1/-1' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 6 }}>Plan Name *</div>
                  <input value={planForm.name} onChange={e=>setPlanForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Gold Membership" style={inp}/>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 6 }}>Price *</div>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: C.muted }}>$</span>
                    <input type="number" value={planForm.price} onChange={e=>setPlanForm(f=>({...f,price:e.target.value}))} placeholder="0.00" style={{ ...inp, paddingLeft: 24 }}/>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 6 }}>Billing Interval</div>
                  <select value={planForm.billing_interval} onChange={e=>setPlanForm(f=>({...f,billing_interval:e.target.value}))} style={inp}>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 6 }}>Visits Per Period</div>
                  <input type="number" value={planForm.visits_per_period} onChange={e=>setPlanForm(f=>({...f,visits_per_period:e.target.value}))} placeholder="Leave blank = unlimited" style={inp}/>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 6 }}>Description</div>
                  <input value={planForm.description} onChange={e=>setPlanForm(f=>({...f,description:e.target.value}))} placeholder="What's included..." style={inp}/>
                </div>
                <div style={{ gridColumn: '1/-1' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 6 }}>Perks / Benefits</div>
                  <input value={planForm.perks} onChange={e=>setPlanForm(f=>({...f,perks:e.target.value}))} placeholder="e.g. 10% off all products, free rolling papers..." style={inp}/>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={closePlanModal} style={{ flex:1, padding:12, background:'transparent', color:C.muted, border:'1px solid '+C.border, borderRadius:10, fontWeight:600, cursor:'pointer' }}>Cancel</button>
                <button onClick={savePlan} style={{ flex:2, padding:12, background:C.amber, color:'#000', border:'none', borderRadius:10, fontWeight:800, cursor:'pointer' }}>{editPlan ? 'Update Plan' : 'Create Plan'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enroll Modal */}
      {showEnroll && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: C.panel, border: '1px solid ' + C.border, borderRadius: 16, width: '100%', maxWidth: 480, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid ' + C.border, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 17, fontWeight: 800 }}>👑 Enroll Member</div>
              <button onClick={() => { setShowEnroll(false); setEnrollForm({ contact_id: '', plan_id: '', notes: '' }); }} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 22, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 6 }}>Customer *</div>
                <CustomerDropdown customers={customers} value={enrollForm.contact_id} onChange={id => setEnrollForm(f=>({...f,contact_id:id}))} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 8 }}>Select Plan *</div>
                {plans.filter(p => p.is_active).length === 0 ? (
                  <div style={{ color: C.muted, padding: 16, textAlign: 'center', fontSize: 13 }}>No active plans. Create one first.</div>
                ) : plans.filter(p => p.is_active).map(p => (
                  <div key={p.id} onClick={() => setEnrollForm(f=>({...f,plan_id:p.id}))}
                    style={{ display: 'flex', alignItems: 'center', padding: '14px 16px', border: '2px solid ' + (enrollForm.plan_id === p.id ? C.amber : C.border), borderRadius: 12, marginBottom: 8, cursor: 'pointer', background: enrollForm.plan_id === p.id ? 'rgba(245,166,35,0.06)' : 'transparent', transition: 'all .15s' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, color: C.text }}>{p.name}</div>
                      <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{p.visits_per_period ? p.visits_per_period + ' visits' : 'Unlimited'} / {p.billing_interval}</div>
                      {p.perks && <div style={{ fontSize: 11, color: C.purple, marginTop: 2 }}>✨ {p.perks}</div>}
                    </div>
                    <div style={{ fontWeight: 900, fontSize: 20, color: C.amber }}>${parseFloat(p.price).toFixed(2)}</div>
                    {enrollForm.plan_id === p.id && <div style={{ marginLeft: 10, fontSize: 18, color: C.amber }}>✓</div>}
                  </div>
                ))}
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 6 }}>Notes</div>
                <input value={enrollForm.notes} onChange={e=>setEnrollForm(f=>({...f,notes:e.target.value}))} placeholder="Optional notes..." style={inp}/>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setShowEnroll(false)} style={{ flex:1, padding:12, background:'transparent', color:C.muted, border:'1px solid '+C.border, borderRadius:10, fontWeight:600, cursor:'pointer' }}>Cancel</button>
                <button onClick={enrollMember} style={{ flex:2, padding:12, background:C.amber, color:'#000', border:'none', borderRadius:10, fontWeight:800, cursor:'pointer' }}>👑 Enroll Member</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
